import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ExternalLink, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import {
  formatDate, formatDateTime, formatNumber, metricsForPlatform, METRIC_KEYS, METRIC_LABELS, todayKL,
} from "@/lib/format";
import { PageHeader } from "@/components/portal/PortalLayout";
import {
  Attachment, Badge, Button, Card, Empty, ErrorNote, Field, Loading, Modal, Note,
  ProgressBar, inputClass,
} from "@/components/portal/Ui";
import { Uploader } from "@/components/portal/Uploader";
import { ScreenshotReader } from "@/components/portal/ScreenshotReader";
import {
  CategoryBadge, PLATFORMS, PlatformBadge, PlatformTabs, ReviewBadge, isUgc,
} from "@/components/portal/PlatformBits";

/** Posts expected of this creator against what they have already submitted. */
export function postProgress(expected, submitted) {
  const target = Number.isFinite(Number(expected)) && Number(expected) > 0 ? Number(expected) : null;
  if (target == null) {
    return { target: null, submitted, remaining: 0, over: 0, complete: false };
  }
  return {
    target,
    submitted,
    remaining: Math.max(0, target - submitted),
    over: Math.max(0, submitted - target),
    complete: submitted >= target,
  };
}

const plural = (n, word) => `${word}${n === 1 ? "" : "s"}`;

/** One short line: the campaign's posting position, never alarming. */
function ProgressLine({ progress, testId }) {
  if (progress.target == null) {
    return (
      <span className="text-xs font-semibold text-[#8a8378]" data-testid={testId}>
        No fixed post count
      </span>
    );
  }
  if (progress.over > 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5d7a53]" data-testid={testId}>
        <Check className="h-3.5 w-3.5" />
        {progress.submitted} submitted of {progress.target} expected ·{" "}
        {progress.over} over-delivered
      </span>
    );
  }
  if (progress.complete) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5d7a53]" data-testid={testId}>
        <Check className="h-3.5 w-3.5" />
        All {progress.target} expected {plural(progress.target, "post")} submitted
      </span>
    );
  }
  return (
    <span className="text-xs font-semibold text-[#F2811D]" data-testid={testId}>
      {progress.submitted} of {progress.target} {plural(progress.target, "post")} submitted ·{" "}
      {progress.remaining} {plural(progress.remaining, "post")} left
    </span>
  );
}

export default function CreatorCampaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState(null);
  const [posts, setPosts] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [postModal, setPostModal] = useState(null);
  const [insightModal, setInsightModal] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [c, p, f] = await Promise.all([
        api.get("/campaigns"),
        api.get("/posts"),
        api.get("/feedback", { params: { target_type: "insight" } }),
      ]);
      setCampaigns(c.data);
      setPosts(p.data);
      setFeedback(f.data);
      setError("");
    } catch (e) {
      setError(apiError(e));
      setCampaigns((prev) => prev || []);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const postsByCampaign = useMemo(() => {
    const map = {};
    posts.forEach((post) => {
      map[post.campaign_id] = map[post.campaign_id] || [];
      map[post.campaign_id].push(post);
    });
    return map;
  }, [posts]);

  const feedbackFor = (insightId) => feedback.filter((f) => f.target_id === insightId);

  if (campaigns === null) return <Loading label="Loading your campaigns…" />;

  return (
    <div data-testid="creator-campaigns-page">
      <PageHeader
        title="My Campaigns"
        description="Submit your content links for Instagram, TikTok, XiaoHongShu and Threads, then add the insight numbers for each one. Everything here is private to you and the AnyTribe team."
        onRefresh={load}
        refreshing={refreshing}
      />
      <ErrorNote message={error} onRetry={load} />

      {campaigns.length === 0 ? (
        <Empty
          testId="creator-no-campaigns"
          title="No campaigns assigned yet"
          body="Once AnyTribe assigns you to a campaign it will appear here with the brief and your deadlines."
        />
      ) : (
        <div className="mt-5 space-y-5">
          {campaigns.map((campaign) => {
            const mine = campaign.my_assignment || {};
            const campaignPosts = postsByCampaign[campaign.id] || [];
            const missingInsights = campaignPosts.filter((p) => !p.latest_insight).length;
            const needsCorrection = campaignPosts.filter(
              (p) => p.latest_insight?.needs_correction
            ).length;
            const expected = mine.expected_posts;
            const progress = postProgress(expected, campaignPosts.length);
            const ugc = isUgc(campaign.category);

            return (
              <Card key={campaign.id} testId={`creator-campaign-${campaign.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">
                      {campaign.client_org_name}
                    </p>
                    <h2 className="mt-1 text-xl font-extrabold">{campaign.name}</h2>
                    {!ugc && (
                      <p className="mt-1.5">
                        <ProgressLine progress={progress} testId={`creator-progress-line-${campaign.id}`} />
                      </p>
                    )}
                    <p className="mt-1.5 text-sm body-text">
                      {formatDate(campaign.start_date)} – {formatDate(campaign.end_date)}
                      {campaign.event_date && (
                        <span className="mt-1 block font-semibold text-[#F2811D]" data-testid={`creator-campaign-event-date-${campaign.id}`}>
                          Event date {formatDate(campaign.event_date)}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <CategoryBadge category={campaign.category} testId={`creator-campaign-category-${campaign.id}`} />
                    <Badge testId={`creator-campaign-status-${campaign.id}`}>{campaign.status}</Badge>
                  </div>
                </div>

                {campaign.brief && (
                  <div className="mt-4 rounded-xl bg-[#FBFAF7] p-4">
                    <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-[#8a8378]">
                      Instructions
                    </h3>
                    <p className="mt-2 whitespace-pre-line text-sm leading-relaxed body-text">
                      {campaign.brief}
                    </p>
                  </div>
                )}

                {isUgc(campaign.category) ? (
                  <div className="mt-4" data-testid={`ugc-notice-${campaign.id}`}>
                    <Note tone="orange">
                      This is a UGC content creation campaign. Posting links and performance
                      insights are not required. You can proceed directly to submit your invoice.
                    </Note>
                    <div className="mt-4">
                      <Button testId={`ugc-invoice-button-${campaign.id}`} onClick={() => navigate("/creator/invoices")}>
                        Submit invoice
                      </Button>
                    </div>
                  </div>
                ) : (
                <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                      Expected posts
                    </dt>
                    <dd className="mt-1 text-lg font-extrabold tabular" data-testid={`creator-expected-count-${campaign.id}`}>
                      {progress.target == null ? (
                        <span className="text-sm font-semibold body-text">No fixed count</span>
                      ) : (
                        progress.target
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                      Submitted
                    </dt>
                    <dd className="mt-1 text-lg font-extrabold tabular" data-testid={`creator-published-count-${campaign.id}`}>
                      {campaignPosts.length}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                      Remaining
                    </dt>
                    <dd className="mt-1 text-lg font-extrabold tabular" data-testid={`creator-remaining-count-${campaign.id}`}>
                      {progress.target == null ? (
                        <span className="text-sm font-semibold body-text">—</span>
                      ) : progress.remaining > 0 ? (
                        <span className="text-[#F2811D]">{progress.remaining}</span>
                      ) : (
                        0
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                      Posting deadline
                    </dt>
                    <dd className="mt-1 text-sm font-semibold">{formatDate(mine.posting_deadline)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                      Insight deadline
                    </dt>
                    <dd className="mt-1 text-sm font-semibold">{formatDate(mine.insight_deadline)}</dd>
                  </div>
                </dl>
                )}

                {progress.target != null && !ugc && (
                  <div className="mt-4 space-y-2">
                    <ProgressBar percent={(campaignPosts.length / progress.target) * 100} />
                    {progress.complete && missingInsights === 0 && (
                      <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5d7a53]"
                         data-testid={`creator-progress-complete-${campaign.id}`}>
                        <Check className="h-3.5 w-3.5" />
                        {progress.over > 0
                          ? `All expected posts submitted, plus ${progress.over} more — thank you.`
                          : "All expected posts submitted."}
                      </p>
                    )}
                  </div>
                )}

                {!isUgc(campaign.category) && (missingInsights > 0 || needsCorrection > 0) && (
                  <div className="mt-4 space-y-2">
                    {missingInsights > 0 && (
                      <Note tone="orange" testId={`creator-missing-insights-${campaign.id}`}>
                        {missingInsights} submitted post{missingInsights === 1 ? "" : "s"} still
                        {missingInsights === 1 ? " needs" : " need"} insight numbers. Invoicing for
                        this campaign opens once every submitted post has its insights attached.
                      </Note>
                    )}
                    {needsCorrection > 0 && (
                      <Note tone="red">
                        {needsCorrection} insight submission{needsCorrection === 1 ? "" : "s"} need
                        {needsCorrection === 1 ? "s" : ""} correcting — see the notes below.
                      </Note>
                    )}
                  </div>
                )}

                <div className="mt-5 flex flex-wrap gap-2.5">
                  {!isUgc(campaign.category) && (
                  <Button
                    testId={`add-post-button-${campaign.id}`}
                    onClick={() => setPostModal(campaign)}
                  >
                    <Plus className="h-4 w-4" />
                    Submit content
                  </Button>
                  )}
                  {!isUgc(campaign.category) && (
                  <Button
                    variant="outline"
                    testId={`submit-insights-button-${campaign.id}`}
                    onClick={() => setInsightModal({ campaign, post: campaignPosts[0] || null })}
                    disabled={campaignPosts.length === 0}
                  >
                    Submit insights
                  </Button>
                  )}
                  {!isUgc(campaign.category) && (
                  <Button
                    variant="outline"
                    testId={`go-invoices-button-${campaign.id}`}
                    onClick={() => navigate(`/creator/invoices?campaign=${campaign.id}`)}
                  >
                    Submit invoice
                  </Button>
                  )}
                </div>

                {campaignPosts.length > 0 && (
                  <div className="mt-6 space-y-3 border-t border-[#F0ECE4] pt-5">
                    <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                      My content submissions
                    </h3>
                    <CampaignSubmissions
                      campaign={campaign}
                      posts={campaignPosts}
                      feedbackFor={feedbackFor}
                      onAddInsight={(post) => setInsightModal({ campaign, post })}
                    />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <PostModal
        campaign={postModal}
        progress={
          postModal
            ? postProgress(
                postModal.my_assignment?.expected_posts,
                (postsByCampaign[postModal.id] || []).length
              )
            : null
        }
        onClose={() => setPostModal(null)}
        onSaved={async (post) => {
          setPostModal(null);
          await load();
          setInsightModal({ campaign: postModal, post });
        }}
      />
      <InsightModal
        state={insightModal}
        posts={insightModal ? postsByCampaign[insightModal.campaign.id] || [] : []}
        onClose={() => setInsightModal(null)}
        onSaved={async () => {
          setInsightModal(null);
          await load();
        }}
      />
    </div>
  );
}

function CampaignSubmissions({ posts, feedbackFor, onAddInsight }) {
  const [tab, setTab] = useState("all");
  const counts = useMemo(() => {
    const out = {};
    PLATFORMS.forEach((p) => {
      out[p] = posts.filter((post) => post.platform === p).length;
    });
    return out;
  }, [posts]);
  const shown = tab === "all" ? posts : posts.filter((post) => post.platform === tab);

  return (
    <div className="space-y-3">
      <PlatformTabs value={tab} onChange={setTab} counts={counts} idPrefix="creator" />
      {shown.length === 0 ? (
        <p className="text-sm body-text" data-testid="creator-no-platform-posts">
          Nothing submitted on this platform yet.
        </p>
      ) : (
        shown.map((post) => {
          const insight = post.latest_insight;
          const notes = insight ? feedbackFor(insight.id) : [];
          return (
            <div
              key={post.id}
              className="rounded-xl border border-[#EFEAE1] p-4"
              data-testid={`creator-post-${post.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <PlatformBadge platform={post.platform} testId={`creator-post-platform-${post.id}`} />
                    <ReviewBadge status={post.review_status} testId={`creator-post-review-${post.id}`} />
                    <span className="text-xs body-text">Posted {formatDate(post.posting_date)}</span>
                  </div>
                  <a
                    href={post.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`creator-post-link-${post.id}`}
                    className="mt-2 inline-flex max-w-full items-center gap-1.5 truncate text-sm font-semibold text-[#F2811D] hover:underline"
                  >
                    <span className="truncate">{post.url}</span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                  </a>
                </div>
                <Button
                  variant="outline"
                  testId={`add-insight-button-${post.id}`}
                  onClick={() => onAddInsight(post)}
                >
                  {insight ? "Update insights" : "Add insights"}
                </Button>
              </div>

              {post.review_status === "Changes requested" && post.review_note && (
                <div className="mt-3">
                  <Note tone="red" testId={`creator-review-note-${post.id}`}>
                    <strong>{post.reviewed_by_name || "AnyTribe"}:</strong> {post.review_note}
                  </Note>
                </div>
              )}

              {post.content_files?.length > 0 && (
                <div className="mt-3">
                  <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                    Content files
                  </p>
                  <div className="mt-2 flex flex-wrap gap-4">
                    {post.content_files.map((file) => (
                      <Attachment key={file.id} file={file} testId={`content-file-${file.id}`} />
                    ))}
                  </div>
                </div>
              )}

              {insight ? (
                <div className="mt-4">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <Badge tone={insight.needs_correction ? "red" : "green"}>
                      {insight.needs_correction ? "Needs correction" : "Reported"}
                    </Badge>
                    <span className="text-xs body-text">
                      Captured {formatDate(insight.capture_date)} · submitted{" "}
                      {formatDateTime(insight.created_at)}
                    </span>
                    {post.insight_history_count > 1 && (
                      <span className="text-xs body-text">
                        {post.insight_history_count} snapshots on record
                      </span>
                    )}
                  </div>
                  <div className="scroll-x mt-3">
                    <div className="flex min-w-max gap-5">
                      {metricsForPlatform(post.platform).map((field) => (
                        <div key={field.key}>
                          <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                            {field.label}
                          </p>
                          <p className="mt-0.5 text-sm font-extrabold tabular">
                            {insight[field.key] == null ? (
                              <span className="font-medium body-text">Not provided</span>
                            ) : (
                              formatNumber(insight[field.key])
                            )}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {insight.screenshots?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                        Insight screenshots
                      </p>
                      <div className="mt-2 flex flex-wrap gap-4">
                        {insight.screenshots.map((file) => (
                          <Attachment key={file.id} file={file} testId={`insight-file-${file.id}`} />
                        ))}
                      </div>
                    </div>
                  )}
                  {notes.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {notes.map((note) => (
                        <Note
                          key={note.id}
                          tone={note.kind === "resolved" ? "green" : "red"}
                          testId={`insight-feedback-${note.id}`}
                        >
                          <strong>{note.author_name}:</strong> {note.message}
                        </Note>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-3 text-sm body-text">No insights submitted for this post yet.</p>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

export function PostModal({ campaign, onClose, onSaved, creators, creatorId, setCreatorId, progress }) {
  const [platform, setPlatform] = useState("Instagram");
  const [url, setUrl] = useState("");
  const [postingDate, setPostingDate] = useState(todayKL());
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [uploaderKey, setUploaderKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (campaign) {
      setPlatform("Instagram");
      setUrl("");
      setPostingDate(todayKL());
      setNote("");
      setFiles([]);
      setUploaderKey((k) => k + 1);
      setError("");
    }
  }, [campaign]);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/posts", {
        campaign_id: campaign.id,
        creator_id: creatorId || undefined,
        platform,
        url: url.trim(),
        posting_date: postingDate,
        note: note.trim() || null,
        content_file_ids: files.map((f) => f.id),
      });
      toast.success("Content submission saved — AnyTribe will review it");
      onSaved(data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(campaign)}
      onClose={onClose}
      title="Submit content"
      testId="post-modal"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="post-form">
        <ErrorNote message={error} />
        {progress?.target != null && (
          <p
            className={`text-sm font-semibold ${
              progress.submitted >= progress.target ? "text-[#5d7a53]" : "text-[#F2811D]"
            }`}
            data-testid="post-progress-note"
          >
            {progress.submitted >= progress.target
              ? `Post ${progress.submitted + 1} — over and above the ${progress.target} expected.`
              : `Post ${progress.submitted + 1} of ${progress.target} for this campaign.`}
          </p>
        )}
        {creators && (
          <Field label="Creator" required>
            <select
              value={creatorId || ""}
              onChange={(e) => setCreatorId(e.target.value)}
              required
              className={inputClass}
              data-testid="post-creator-select"
            >
              <option value="">Select a creator</option>
              {creators.map((c) => (
                <option key={c.creator_id} value={c.creator_id}>
                  {c.creator_name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Platform" required>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className={inputClass}
            data-testid="post-platform-select"
          >
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Published content link" required hint="Must start with http:// or https://">
          <input
            type="url"
            required
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.instagram.com/reel/..."
            className={inputClass}
            data-testid="post-url-input"
          />
        </Field>
        <Field label="Posting date" required>
          <input
            type="date"
            required
            value={postingDate}
            onChange={(e) => setPostingDate(e.target.value)}
            className={inputClass}
            data-testid="post-date-input"
          />
        </Field>
        <Field
          label="Content files (optional)"
          hint="The creative itself — JPG, PNG, MP4 or MOV, up to 10MB each. Insight screenshots go in the insights step."
        >
          <Uploader key={uploaderKey} kind="content" multiple files={files} onChange={setFiles} testId="content-uploader" />
        </Field>
        <Field label="Note (optional)">
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputClass}
            data-testid="post-note-input"
          />
        </Field>
        <div className="flex justify-end gap-2.5 pt-1">
          <Button variant="outline" onClick={onClose} testId="post-cancel-button">
            Cancel
          </Button>
          <Button type="submit" busy={busy} testId="post-submit-button">
            Save submission
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function InsightModal({ state, posts, onClose, onSaved }) {
  const [postId, setPostId] = useState("");
  const [captureDate, setCaptureDate] = useState(todayKL());
  const [metrics, setMetrics] = useState({});
  const [unavailable, setUnavailable] = useState({});
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [uploaderKey, setUploaderKey] = useState(0);

  useEffect(() => {
    if (state) {
      setPostId(state.post?.id || "");
      setCaptureDate(todayKL());
      setMetrics({});
      setUnavailable({});
      setNote("");
      setFiles([]);
      setError("");
      setUploaderKey((k) => k + 1);
    }
  }, [state]);

  const selectedPost = posts.find((p) => p.id === postId);
  const fields = metricsForPlatform(selectedPost?.platform);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!postId) {
      setError("Select which published post these numbers belong to.");
      return;
    }
    if (files.length === 0) {
      setError("Attach at least one screenshot of the post insights.");
      return;
    }
    if (note.trim().length < 3) {
      setError("A remark is required. Note anything that explains these numbers.");
      return;
    }
    const missing = fields.filter(
      (f) => !unavailable[f.key] && (metrics[f.key] === "" || metrics[f.key] == null)
    );
    if (missing.length > 0) {
      setError(
        `Enter a number for ${missing.map((f) => f.label).join(", ")}, or tick "Not available" ` +
          "if the platform does not show it. A real 0 is fine."
      );
      return;
    }
    const invalid = fields.filter(
      (f) => !unavailable[f.key] && !Number.isInteger(Number(metrics[f.key]))
    );
    if (invalid.length > 0) {
      setError(`${invalid.map((f) => f.label).join(", ")} must be a whole number, 0 or more.`);
      return;
    }
    setBusy(true);
    setError("");
    const payload = {
      post_id: postId,
      capture_date: captureDate,
      note: note.trim(),
      screenshot_file_ids: files.map((f) => f.id),
    };
    const applicable = new Set(fields.map((f) => f.key));
    METRIC_KEYS.forEach((key) => {
      const raw = applicable.has(key) && !unavailable[key] ? metrics[key] : null;
      payload[key] = raw === "" || raw == null ? null : Number(raw);
    });
    payload.unavailable = fields.filter((f) => unavailable[f.key]).map((f) => f.key);
    try {
      await api.post("/insights", payload);
      toast.success("Insights submitted");
      onSaved();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={Boolean(state)} onClose={onClose} title="Submit post insights" wide testId="insight-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="insight-form">
        <ErrorNote message={error} />
        <Note tone="orange">
          Every number below is required. Enter the current cumulative figure shown for this post,
          not the increase since your last submission. If the platform genuinely does not show a
          metric, tick <strong>Not available</strong> — that is stored differently from 0, which
          means a confirmed zero.
        </Note>

        <Field label="Published post" required>
          <select
            value={postId}
            onChange={(e) => setPostId(e.target.value)}
            required
            className={inputClass}
            data-testid="insight-post-select"
          >
            <option value="">Select a post</option>
            {posts.map((post) => (
              <option key={post.id} value={post.id}>
                {post.platform} · {formatDate(post.posting_date)} · {post.url.slice(0, 60)}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Metrics capture date" required>
          <input
            type="date"
            required
            value={captureDate}
            onChange={(e) => setCaptureDate(e.target.value)}
            className={inputClass}
            data-testid="insight-capture-date-input"
          />
        </Field>

        {selectedPost && (
          <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#8A8276]"
             data-testid="insight-platform-label">
            {selectedPost.platform} metrics
          </p>
        )}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {fields.map((field) => (
            <Field key={field.key} label={`${field.label} *`} hint={field.hint}>
              <input
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                disabled={Boolean(unavailable[field.key])}
                value={unavailable[field.key] ? "" : metrics[field.key] ?? ""}
                onChange={(e) => setMetrics((m) => ({ ...m, [field.key]: e.target.value }))}
                placeholder={unavailable[field.key] ? "Not available" : "e.g. 12500"}
                className={`${inputClass} disabled:bg-[#F4F1EB] disabled:text-[#8a8378]`}
                data-testid={`insight-${field.key}-input`}
              />
              <label className="mt-1.5 flex items-center gap-1.5 text-xs body-text">
                <input
                  type="checkbox"
                  checked={Boolean(unavailable[field.key])}
                  onChange={(e) =>
                    setUnavailable((u) => ({ ...u, [field.key]: e.target.checked }))
                  }
                  data-testid={`insight-${field.key}-unavailable`}
                />
                Not available
              </label>
            </Field>
          ))}
        </div>

        <Field label="Insight screenshots" required hint="At least one screenshot is required.">
          <Uploader key={uploaderKey} kind="screenshot" multiple files={files} onChange={setFiles} testId="insight-uploader" />
        </Field>

        <ScreenshotReader
          fileIds={files.map((f) => f.id)}
          platform={selectedPost?.platform}
          testId="insight-reader"
          onRead={(data) => {
            setMetrics((current) => ({ ...current, ...data.values }));
            setUnavailable((current) => {
              const next = { ...current };
              Object.keys(data.values).forEach((key) => delete next[key]);
              return next;
            });
            if (data.capture_date) setCaptureDate(data.capture_date);
          }}
        />

        <Field label="Remark" required hint="Explain anything that affects these numbers — a boost, a repost, a metric the platform did not show.">
          <textarea
            rows={2}
            required
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputClass}
            placeholder="e.g. Reach not shown for this post type; numbers captured 7 days after posting."
            data-testid="insight-note-input"
          />
        </Field>

        <div className="flex justify-end gap-2.5 pt-1">
          <Button variant="outline" onClick={onClose} testId="insight-cancel-button">
            Cancel
          </Button>
          <Button type="submit" busy={busy} testId="insight-submit-button">
            Submit insights
          </Button>
        </div>
      </form>
    </Modal>
  );
}
